import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { customerApiClient } from "../api/customerHttpClient";

const LocationContext = createContext();

// Delhi NCR fallback — used for first-time visitors with no stored location.
// Ensures the feed shows geographically relevant content without a GPS prompt.
const DELHI_NCR_DEFAULTS = {
  latitude: "28.6139",
  longitude: "77.2090",
  displayAddress: "Delhi NCR",
  pincode: "",
};

export const LocationProvider = ({ children }) => {
  const storedLat = localStorage.getItem("latitude") || "";
  const storedLng = localStorage.getItem("longitude") || "";

  // Seed with Delhi NCR if no location is on record so the feed is never empty.
  const [latitude, setLatitude] = useState(storedLat || DELHI_NCR_DEFAULTS.latitude);
  const [longitude, setLongitude] = useState(storedLng || DELHI_NCR_DEFAULTS.longitude);
  const [displayAddress, setDisplayAddress] = useState(
    localStorage.getItem("displayAddress") || (storedLat ? "" : DELHI_NCR_DEFAULTS.displayAddress)
  );
  const [pincode, setPincode] = useState(localStorage.getItem("pincode") || "");

  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const formData = new FormData();
      formData.append("latitude", lat);
      formData.append("longitude", lng);

      const response = await customerApiClient.post(
        `/APIs/Web/reverseGeocodeLocation`,
        formData,
      );

      if (response?.data?.status && response?.data?.data) {
        const data = response.data.data;
        const addressText = data.display || data.full || "";
        const pinText = data.pincode || "";
        if (addressText) {
          setDisplayAddress(addressText);
          localStorage.setItem("displayAddress", addressText);
        }
        if (pinText) {
          setPincode(pinText);
          localStorage.setItem("pincode", pinText);
        }
      }
    } catch (error) {}
  }, []);

  useEffect(() => {
    localStorage.setItem("latitude", latitude || "");
  }, [latitude]);

  useEffect(() => {
    localStorage.setItem("longitude", longitude || "");
  }, [longitude]);

  useEffect(() => {
    localStorage.setItem("displayAddress", displayAddress || "");
  }, [displayAddress]);

  useEffect(() => {
    localStorage.setItem("pincode", pincode || "");
  }, [pincode]);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    let lat = latitude;
    let lng = longitude;

    if ((!lat || !lng) && userData?.latitude && userData?.longitude) {
      lat = String(userData.latitude);
      lng = String(userData.longitude);
      setLatitude(lat);
      setLongitude(lng);
    }

    if ((!lat || !lng) && "geolocation" in navigator && navigator.permissions) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (result.state !== "granted") {
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const latNew = pos.coords.latitude.toString();
            const lngNew = pos.coords.longitude.toString();
            setLatitude(latNew);
            setLongitude(lngNew);
            if (!displayAddress || !pincode) {
              reverseGeocode(latNew, lngNew);
            }
          },
          () => {},
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 },
        );
      });
      return;
    }

    if (lat && lng && (!displayAddress || !pincode)) {
      reverseGeocode(lat, lng);
    }
  }, [displayAddress, latitude, longitude, pincode, reverseGeocode]);

  return (
    <LocationContext.Provider
      value={{
        latitude,
        longitude,
        displayAddress,
        pincode,
        setLatitude,
        setLongitude,
        setDisplayAddress,
        setPincode,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useGeoLocation = () => useContext(LocationContext);
